import { DateInput } from "@msuite/ui/date-input";
import { Combobox, Select, TextInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { WeekdayPicker } from "@msuite/ui/weekday-picker";
import { guardString } from "@msuite/utils/strings";
import type { CrewDefinitionFormSchema } from "@msuite/utils/schemas";
import React, { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useAuth } from "@/hooks/use-auth";
import { usePartnerships } from "@/hooks/use-partnerships";
import { useVehicles } from "@/hooks/use-vehicles";
import { CrewDefinitionFormStaffing } from "./crew-definition-form-staffing";

/** Props Interface */
interface CrewDefinitionFormProps {
	crewDefinitionId?: string;
}

export function CrewDefinitionForm({ crewDefinitionId }: CrewDefinitionFormProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { organization } = useAuth();
	const methods = useFormContext<CrewDefinitionFormSchema>();

	/** Query */
	const { vehicles } = useVehicles();
	// Only the active partnerships this organisation hosts: the host raises the crew and the
	// partner staffs it, so a partnership we are merely a guest of offers no crew to define.
	const { partnerships, isFetching } = usePartnerships("host", ["active"]);

	/** State */
	const organizationId = useWatch({ control: methods.control, name: "organizationId" });

	/** Computed */
	const isOwnCrew = organizationId === organization?.id;

	/** Effects */
	// Moving a crew to a partner drops what this organisation was putting on it. The fields are
	// hidden either way, but their values would still travel with the save and the handler refuses
	// them — so a deliberate change would fail on the leftovers of the previous choice.
	useEffect(() => {
		if (isOwnCrew) return;
		methods.setValue("vehicleId", null);
		methods.setValue("leadUserId", null);
		methods.setValue("memberUserIds", []);
	}, [isOwnCrew, methods]);

	/** Render */
	return (
		<React.Fragment>
			{/* Optional: an unnamed crew is called after its partner or its foreman, which is how a
			    yard refers to it anyway. */}
			<TextInput
				control={methods.control}
				name="name"
				label={t("general.name")}
				isRequired={false}
				placeholder={t("planning.crews.crew_name_placeholder")}
			/>
			{/* The same list the board's add-crew popover offers, in the same order: this
			    organisation first, then every partner it hosts. */}
			<Select
				control={methods.control}
				name="organizationId"
				label={t("organization.company")}
				isDisabled={isFetching}
				isRequired
				isNullable={false}
				helperText={t("planning.crews.crew_partner_helper_text")}
				options={[
					{
						value: guardString(organization?.id, "#"),
						label: t("planning.crews.own_crew"),
						item: {},
					},
				].concat(
					partnerships?.map((partnership) => ({
						value: partnership.partnerOrgId,
						label: guardString(partnership._partner?.name),
						item: {},
					})) ?? [],
				)}
				itemNotFoundLabel={undefined}
			/>
			{/* The two ends of the span read as one control, so they sit on one row. */}
			<div className="gap-4 grid grid-cols-2">
				<DateInput
					control={methods.control}
					name="startDate"
					label={t("planning.crews.crew_start_date")}
					isRequired
				/>
				<DateInput
					control={methods.control}
					name="endDate"
					label={t("planning.crews.crew_end_date")}
					isRequired={false}
				/>
			</div>
			{/* Which days this crew turns up on. It is a bound on scheduling rather than a preference:
			    a crew is refused a stretch it would not work every day of, so the board never shows a
			    ticket as covered by somebody who is not coming. */}
			<WeekdayPicker
				control={methods.control}
				name="weekdays"
			/>
			{/* A partner's crew is staffed and equipped by the partner — on people this organisation
			    cannot see and lorries it does not own — so neither field is offered for one. */}
			{isOwnCrew && (
				<React.Fragment>
					<Combobox
						control={methods.control}
						name="vehicleId"
						label={t("vehicles.vehicle")}
						placeholder={t("vehicles.select_vehicle")}
						isRequired={false}
						isNullable
						keyExtractor={(item) => item.value}
						displayValue={(item) => guardString(item.label)}
						options={vehicles.map((vehicle) => ({
							label: guardString(vehicle.licensePlate),
							value: vehicle.id,
						}))}
					/>
					<CrewDefinitionFormStaffing crewDefinitionId={crewDefinitionId} />
				</React.Fragment>
			)}
		</React.Fragment>
	);
}
